import React, { useEffect, useState } from "react";
import { API_HEADERS, MIDDLEWARE } from "../utils/utils";

const useDownload = ({middleware}:{middleware:string}) => {
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const [downloadsData, setDownloadsData] = useState({});

  useEffect(() => {
    const fetchPollingData = async () => {
      const stored = sessionStorage.getItem("dowloadsData");
      if (!stored || stored === "{}") return;

      let parsed = {};
      try {
        parsed = JSON.parse(stored);
      } catch {
        return;
      }

      const hasInProgress = Object.values(parsed).some(
        (item:any) => item?.status === "InProgress"
      );

      if (!hasInProgress) return;

      try {
        const response = await fetch(
          `${
            middleware || MIDDLEWARE 
          }/edge-folder-management/api/polling/excel`,
          {
            method: "POST",
            headers: {
              ...API_HEADERS,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(Object.keys(parsed)),
          }
        );

        if (!response.ok) throw new Error("Polling failed");

        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          const json = JSON.stringify(data);
          sessionStorage.setItem("dowloadsData", json);

          const channel = new BroadcastChannel("downloads-sync");
          channel.postMessage({ type: "SYNC_DOWNLOADS", payload: json });
          channel.close();
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    };

    const checkAndStartPolling = () => {
      const existing = sessionStorage.getItem("dowloadsData");
      if (!existing || existing === "{}") return;

      fetchPollingData();
      const intervalId = setInterval(fetchPollingData, 5000);
      return intervalId;
    };

    let checkInterval: any = setInterval(() => {
      const exists = sessionStorage.getItem("dowloadsData");
      if (exists && exists !== "{}") {
        const pollInterval = checkAndStartPolling();
        clearInterval(checkInterval);
        checkInterval = null;
        return () => clearInterval(pollInterval);
      }
    }, 500);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
    };
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel("downloads-sync");
    let lastSessionValue = sessionStorage.getItem("dowloadsData") || "{}";

    channel.onmessage = (event) => {
      const { type, payload } = event.data || {};

      if (type === "SYNC_DOWNLOADS" && payload) {
        const prevStored = sessionStorage.getItem("dowloadsData") || "{}";
        if (prevStored !== payload) {
          sessionStorage.setItem("dowloadsData", payload);
          const parsed = JSON.parse(payload);
          setDownloadsData((prev) => {
            const prevStr = JSON.stringify(prev);
            return prevStr !== payload ? parsed : prev;
          });
          lastSessionValue = payload;
        }
      }

      if (type === "REQUEST_INITIAL_SYNC") {
        const current = sessionStorage.getItem("dowloadsData") || "{}";
        if (current !== "{}") {
          channel.postMessage({ type: "INITIAL_SYNC", payload: current });
        }
      }

      if (type === "INITIAL_SYNC" && payload) {
        sessionStorage.setItem("dowloadsData", payload);
        setDownloadsData(JSON.parse(payload));
        lastSessionValue = payload;
      }
    };

    const interval = setInterval(() => {
      const current = sessionStorage.getItem("dowloadsData") || "{}";

      if (current !== lastSessionValue) {
        channel.postMessage({ type: "SYNC_DOWNLOADS", payload: current });
        lastSessionValue = current;
      }

      const parsed = JSON.parse(current);
      setDownloadsData((prev) => {
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(parsed);
        return prevStr !== newStr ? parsed : prev;
      });
    }, 500);

    if (
      !sessionStorage.getItem("dowloadsData") ||
      sessionStorage.getItem("dowloadsData") === "{}"
    ) {
      channel.postMessage({ type: "REQUEST_INITIAL_SYNC" });
    }

    return () => {
      clearInterval(interval);
      channel.close();
    };
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel("already-downloaded-sync");

    channel.onmessage = (event) => {
      const { type, payload } = event.data || {};

      if (
        (type === "SYNC_ALREADY_DOWNLOADED" ||
          type === "INITIAL_SYNC_ALREADY_DOWNLOADED") &&
        payload
      ) {
        sessionStorage.setItem("alreadyDownloaded", payload);
      }

      if (type === "REQUEST_INITIAL_SYNC_ALREADY_DOWNLOADED") {
        const current = sessionStorage.getItem("alreadyDownloaded") || "[]";
        if (current !== "[]") {
          channel.postMessage({
            type: "INITIAL_SYNC_ALREADY_DOWNLOADED",
            payload: current,
          });
        }
      }
    };

    const alreadyDownloadedRawInit =
      sessionStorage.getItem("alreadyDownloaded") || "[]";
    if (alreadyDownloadedRawInit === "[]") {
      channel.postMessage({ type: "REQUEST_INITIAL_SYNC_ALREADY_DOWNLOADED" });
    }

    if (!downloadsData || Object.keys(downloadsData).length === 0) return;

    const alreadyDownloaded = JSON.parse(
      sessionStorage.getItem("alreadyDownloaded") || "[]"
    );
    const newlyDownloaded: any = [];

    Object.entries(downloadsData).forEach(([key, entry]) => {
      const { emailLink } = entry || {} as any;
      if (!emailLink) return;

      const lockKey = `downloadInProgress-${key}`;

      // skip if already downloaded or locked
      if (
        alreadyDownloaded.includes(key) ||
        localStorage.getItem(lockKey) === "true"
      )
        return;

      // lock this key
      localStorage.setItem(lockKey, "true");

      // perform download
      const link = document.createElement("a");
      link.href = emailLink;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      newlyDownloaded.push(key);
    });

    if (newlyDownloaded.length > 0) {
      const updated = Array.from(
        new Set([...alreadyDownloaded, ...newlyDownloaded])
      );
      const updatedStr = JSON.stringify(updated);

      // update alreadyDownloaded before broadcasting
      sessionStorage.setItem("alreadyDownloaded", updatedStr);

      setTimeout(() => {
        channel.postMessage({
          type: "SYNC_ALREADY_DOWNLOADED",
          payload: updatedStr,
        });

        // unlock after safe delay
        newlyDownloaded.forEach((key: any) => {
          const lockKey = `downloadInProgress-${key}`;
          localStorage.removeItem(lockKey);
        });
      }, 200); // 200ms delay to ensure state write before broadcast
    }

    return () => {
      channel.close();
    };
  }, [downloadsData]);

  return {isDownloadOpen, setIsDownloadOpen, downloadsData}
};

export default useDownload;
